import React from "react";

/**
 * Palworld card text, with the game's printed symbols drawn as badges.
 *
 * The scrape keeps the official site's markup, in which a keyword followed by
 * "@" stands for a symbol printed on the card. So
 *
 *   ACT@1Turn@[Consume X Material@, assign 1 Pal] ... deal 500 Damage@.
 *
 * is the black ACT badge, the 1/Turn badge, the wood icon, and the red Damage
 * burst -- which is why the raw string reads like line noise.
 *
 * There is no icon art on the CDN, so each symbol is drawn in CSS in the colour
 * it is printed in and keeps its word. The cards themselves drop the word for
 * the pure-icon symbols ("gets [swords]+500"), but "gets Power +500" is both
 * clearer out of context and searchable, so the word stays.
 */

interface Props {
  text: string | undefined;
  className?: string;
}

/** Shared chip geometry. Sized in em so it tracks whatever text it sits in. */
const CHIP =
  "inline-flex items-center align-middle mx-[0.25em] px-[0.45em] text-[0.8em] font-bold leading-[1.6] whitespace-nowrap";

/** AUTO / ACT / CONT -- the black lozenge that opens an ability. */
const ABILITY = `${CHIP} rounded-full bg-slate-900 text-white uppercase tracking-wider dark:bg-black dark:ring-1 dark:ring-slate-500`;

/** On Deploy / On Attack / On Assign -- the crimson trigger badge. */
const TRIGGER = `${CHIP} rounded-[0.25em] bg-[#c0124d] text-white`;

/** Resource and stat icons, which the cards print as a coloured glyph. */
const icon = (color: string) =>
  `${CHIP} rounded-[0.25em] border ${color} bg-transparent`;

interface Symbol {
  label: React.ReactNode;
  className: string;
}

const SYMBOLS: Record<string, Symbol> = {
  // Ability types.
  "AUTO@": { label: "AUTO", className: ABILITY },
  "ACT@": { label: "ACT", className: ABILITY },
  "CONT@": { label: "CONT", className: ABILITY },

  // Triggers. The markup runs the words together; the cards space them.
  "OnDeploy@": { label: "On Deploy", className: TRIGGER },
  "OnAttack@": { label: "On Attack", className: TRIGGER },
  "OnAssign@": { label: "On Assign", className: TRIGGER },

  // Once-per-turn: a cream badge with the count in green.
  "1Turn@": {
    label: (
      <>
        <span className="text-[#3f9142]">1</span>
        <span>/Turn</span>
      </>
    ),
    className: `${CHIP} rounded-full border border-slate-800 bg-[#f5f0e2] text-slate-900`,
  },

  // Where and when an ability can be used.
  "Hand@": {
    label: "Hand",
    className: `${CHIP} rounded-[0.25em] bg-[#9b2d9b] text-white`,
  },
  "Quick@": {
    label: "Quick",
    className: `${CHIP} rounded-[0.25em] border border-[#d9711f] text-[#d9711f]`,
  },

  // Stats.
  "Power@": {
    label: "Power",
    className: icon("border-slate-500 text-slate-700 dark:text-slate-300"),
  },
  "Strike@": {
    label: "Strike",
    className: icon("border-slate-500 text-slate-700 dark:text-slate-300"),
  },
  "Damage@": {
    label: "Damage",
    className: icon("border-[#c1272d] text-[#c1272d] dark:text-[#f0736f]"),
  },

  // Resources -- wood and food, in their printed colours.
  "Material@": {
    label: "Material",
    className: icon("border-[#8a5a2b] text-[#8a5a2b] dark:text-[#c99a63]"),
  },
  "Ingredient@": {
    label: "Ingredient",
    className: icon("border-[#4a8a3c] text-[#4a8a3c] dark:text-[#8ac47a]"),
  },

  // Pal types, which appear inline as a coloured diamond.
  "Dragon@": {
    label: "Dragon",
    className: icon("border-[#7d4fbf] text-[#7d4fbf] dark:text-[#b391e8]"),
  },
};

/**
 * One pass over every symbol. The deploy cost (◇8) is a diamond gem on the
 * card rather than an "@" keyword, so it is matched here too.
 */
// No key is a prefix of another and "@" is not a metacharacter, so the keys go
// into the alternation as they are.
const SYMBOL_PATTERN = new RegExp(
  `(${Object.keys(SYMBOLS).join("|")}|◇\\d+)`,
  "g",
);

const COST_PATTERN = /^◇\d+$/;

export default function PalworldCardText({
  text,
  className,
}: Props): React.ReactElement | null {
  if (!text) return null;

  return (
    <span className={`whitespace-pre-line ${className ?? ""}`}>
      {text.split(SYMBOL_PATTERN).map((part, index) => {
        const symbol = SYMBOLS[part];
        if (symbol) {
          return (
            <span key={index} className={symbol.className}>
              {symbol.label}
            </span>
          );
        }

        if (COST_PATTERN.test(part)) {
          return (
            <span
              key={index}
              className={`${CHIP} rounded-[0.25em] border border-slate-500 text-slate-700 dark:text-slate-300`}
            >
              {part}
            </span>
          );
        }

        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </span>
  );
}
